import key from 'weak-key';
import { Col, Container, Row, Divider } from '@geberit/gdds';

// styles
import styles from './media-releases.module.scss';

// components
import Release from './release';
import Filter from './filter';
import { Headline, FontWeights, Formats, getFontWeight } from 'components/ContentElements/Headline';

// utils
import { useMediaReleases } from 'utils/hooks/use-media-releases';
import { classNameBuilder } from 'utils/classNameBuilder';
import { useThemeName } from 'utils/hooks/use-theme';
import { useSearchParamsState } from 'utils/hooks/use-search-params-state';
import { useTranslationFunction } from 'utils/hooks/use-translations';
import { useSectionId } from 'utils/hooks/use-section-id';

// years start from 1999
const YEAR_START = 1999;

export interface MediaReleasesProps {
  title?: string;
  subtitle?: string;
  emphasize?: 'title' | 'subtitle';
  contentIndex?: number;
  backgroundColor?: string;
}

export function MediaReleases({
  title,
  subtitle,
  emphasize,
  contentIndex,
  backgroundColor = 'white',
}: Readonly<MediaReleasesProps>) {
  const [params, setParams] = useSearchParamsState(
    ['year', 'adhoc'],
    [`${new Date().getFullYear()}`, '0'],
  );
  const { year, adhoc } = params;
  const allYears = [...Array(new Date().getFullYear() - YEAR_START + 1).keys()]
    .map((y) => y + YEAR_START)
    .sort((a, b) => a - b)
    .reverse()
    .map((y) => ({
      text: `${y}`,
      value: `${y}`,
    }));

  const translate = useTranslationFunction();
  const allAdhoc = [
    { text: translate('group_media_releases_all'), value: '0' },
    { text: translate('group_media_releases_adhoc'), value: '1' },
  ];
  const { media } = useMediaReleases({ year, adhoc });
  const themeName = useThemeName();
  const sectionId = useSectionId(title ?? '', '', false, contentIndex);

  return (
    <div
      className={classNameBuilder(styles.wrapper, styles[`${backgroundColor}Background`])}
      id={sectionId}
    >
      <Container maxContentWidth="78rem">
        {/* headline */}
        {(title || subtitle) && (
          <Row>
            <Col size={[4, 8, 12]}>
              <Headline
                title={title}
                subtitle={subtitle}
                format={Formats.h2}
                titleFontWeight={getFontWeight(
                  emphasize === 'title',
                  themeName,
                  Formats.h2,
                  FontWeights.regular,
                )}
                subtitleFontWeight={getFontWeight(
                  emphasize === 'subtitle',
                  themeName,
                  Formats.h2,
                  FontWeights.regular,
                )}
                className={styles.headline}
                isUppercase
                titlePreviewId="#st_title"
                subtitlePreviewId="#st_subtitle"
              />
            </Col>
          </Row>
        )}

        {/* filters */}
        <Row className={styles.filters}>
          <Filter
            id="year"
            items={allYears}
            value={year}
            setValue={(year) => setParams({ year })}
          />
          <Filter
            id="adhoc"
            items={allAdhoc}
            value={adhoc}
            setValue={(adhoc) => setParams({ adhoc })}
          />
        </Row>

        {/* content */}
        <Row>
          <Col size={[4, 8, 12]} className={styles.divider}>
            <Divider />
          </Col>
        </Row>
        {media.map((release) => (
          <Release key={key(release)} {...release} />
        ))}
      </Container>
    </div>
  );
}
